import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useGetAppVersionQuery } from '../../redux/services/appVersion.jsx';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const MIN_CHECK_GAP_MS = 10 * 1000;

/**
 * Version of the build the page was served with (meta tag rendered by app.blade.php).
 * Empty when the Vite dev server is running or no build exists.
 */
const getLoadedVersion = () =>
  document.querySelector('meta[name="app-version"]')?.content || null;

/**
 * Detects a new frontend deployment and loads it on in-app navigation.
 *
 * The version the page was loaded with is compared with the server version
 * (polling, tab focus, route change). When it differs, the page is reloaded
 * right after the user navigates, so no unsaved form data is lost.
 */
const AppVersionWatcher = () => {
  const { pathname } = useLocation();
  const loadedVersionRef = useRef(getLoadedVersion());
  const isOutdatedRef = useRef(false);
  const lastCheckRef = useRef(0);
  const previousPathnameRef = useRef(pathname);

  const { data: version, refetch } = useGetAppVersionQuery(undefined, {
    pollingInterval: CHECK_INTERVAL_MS,
    skipPollingIfUnfocused: true,
  });

  useEffect(() => {
    // null = Vite dev server or missing build, nothing to compare
    if (!version) return;

    // fallback for pages rendered without the meta tag
    if (loadedVersionRef.current === null) {
      loadedVersionRef.current = version;
      return;
    }

    isOutdatedRef.current = version !== loadedVersionRef.current;
  }, [version]);

  /**
   * @param {boolean} reloadIfOutdated reload as soon as this check finds a new version
   *   (used right after a navigation, when no form data can be lost)
   */
  const checkVersion = useCallback(
    (reloadIfOutdated = false) => {
      if (Date.now() - lastCheckRef.current < MIN_CHECK_GAP_MS) return;

      lastCheckRef.current = Date.now();
      refetch()
        .unwrap()
        .then((serverVersion) => {
          if (
            reloadIfOutdated &&
            serverVersion &&
            loadedVersionRef.current &&
            serverVersion !== loadedVersionRef.current
          ) {
            window.location.reload();
          }
        })
        .catch(() => {
          // background check, failures are ignored
        });
    },
    [refetch],
  );

  useEffect(() => {
    if (previousPathnameRef.current === pathname) return;
    previousPathnameRef.current = pathname;

    if (isOutdatedRef.current) {
      window.location.reload();
      return;
    }

    checkVersion(true);
  }, [pathname, checkVersion]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // only detect the new version, the reload waits for the next navigation
        checkVersion();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkVersion]);

  return null;
};

export default AppVersionWatcher;
